import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Inbox } from "lucide-react";
import Navbar from "../components/Navbar";
import Avatar from "../components/Avatar";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const Header = ({ children, testid }) => (
  <h2 className="social-head" data-testid={testid}><span className="social-star">★</span>{children}<span className="social-star">★</span></h2>
);

export default function FriendsPage() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const timer = useRef(null);

  const loadFriends = () => api.get("/friends").then(({ data }) => setFriends(data.friends || [])).catch(() => {});

  useEffect(() => {
    loadFriends();
    api.get("/friends/pending").then(({ data }) => setPendingCount((data.incoming || []).length)).catch(() => {});
  }, []);

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    const query = v.trim();
    if (query.length < 3) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timer.current = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(query)}`).then(({ data }) => setResults(data.results || [])).catch(() => setResults([])).finally(() => setSearching(false));
    }, 300);
  };

  const addFriend = async (u) => {
    try {
      await api.post("/friends/request", { user_id: u.id });
      toast.success(`Request sent to ${u.name} ★`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send request."); }
  };

  return (
    <div className="social-shell" data-skin="ancientsocial" data-variant="profile" data-testid="friends-page">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 space-y-5">
        <section className="social-panel" data-testid="friends-search-panel">
          <Header testid="friends-search-head">Find Friends</Header>
          <div className="p-4">
            <div className="relative flex items-center">
              <Search size={16} className="pointer-events-none absolute left-3 text-[var(--text-muted)]" />
              <input value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search by name or @username (min 3 letters)"
                className="w-full rounded border border-[var(--border-subtle)] bg-white py-2.5 pl-9 pr-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
                data-testid="friends-search-input" />
            </div>
            {q.trim().length > 0 && q.trim().length < 3 && <p className="mt-2 text-xs text-[var(--text-muted)]">Type at least 3 characters to search.</p>}
            <div className="mt-3 space-y-2" data-testid="friends-search-results">
              {searching && <p className="text-sm text-[var(--text-muted)]">Searching…</p>}
              {!searching && results.map((u) => (
                <div key={u.id} className="flex items-center gap-3 rounded border border-dotted border-[var(--border-subtle)] p-2" data-testid={`friends-search-result-${u.username}`}>
                  <Avatar user={u} size={40} rounded="rounded-[3px]" />
                  <button onClick={() => navigate(`/profile/${u.username}`)} className="min-w-0 flex-1 text-left">
                    <p className="truncate font-bold text-[var(--text-primary)]">{u.name}</p>
                    <p className="truncate text-xs text-[var(--text-muted)]">@{u.username}</p>
                  </button>
                  <button onClick={() => addFriend(u)} className="social-pill" data-testid={`friends-add-${u.username}`}>Add</button>
                </div>
              ))}
              {!searching && q.trim().length >= 3 && results.length === 0 && <p className="text-sm text-[var(--text-muted)]">No users found.</p>}
            </div>
          </div>
        </section>

        <section className="social-panel" data-testid="friends-list-panel">
          <Header testid="friends-list-head">My Friends ({friends.length})</Header>
          <div className="flex items-center justify-between px-4 pt-3">
            <Link to="/friends/requests" className="social-link inline-flex items-center gap-1 text-sm" data-testid="friends-requests-link">
              <Inbox size={14} /> Friend Requests{pendingCount > 0 ? ` (${pendingCount})` : ""}
            </Link>
          </div>
          {friends.length === 0 ? (
            <p className="p-6 text-center text-sm text-[var(--text-secondary)]">No friends yet — search above to connect.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
              {friends.map((u) => (
                <button key={u.id} onClick={() => navigate(`/profile/${u.username}`)} className="flex items-center gap-3 rounded border border-dotted border-[var(--border-subtle)] bg-white/60 p-2 text-left" data-testid={`friend-card-${u.username}`}>
                  <Avatar user={u} size={44} rounded="rounded-[3px]" />
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[var(--text-primary)]">{u.name}</p>
                    <p className="truncate text-xs text-[var(--text-secondary)]">{u.mood || `@${u.username}`}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
